import { useState, useEffect } from "react";
import "../../assets/scss/home-banner/add-home-banner.scss";
import axiosConfig from "../../services/axiosConfig";
import setHeader from "../../components/Functions/setHeader";
import SweetAlert from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";

const AddHomeBanner = () => {
  const navigate = useNavigate()
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    banner_text: "",
    banner_image: null,
    banner_status: true,
    banner_order: ''
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);



  // Fetch banner data for editing
  useEffect(() => {
    if (isEditMode) {
      fetchBannerData();
    }
  }, [id, isEditMode]);

  useEffect(() => {
    getData();
  }, []);


  const getData = () => {
    setLoading(true);
    setData([]); // Reset data before fetching new data

    axiosConfig.get("banner-list", setHeader())
      .then((res) => {
        setData(res.data.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchBannerData = async () => {
    setIsDataLoading(true);
    try {
      const response = await axiosConfig.get(`get-banner/${id}`, setHeader());
      if (!response.data.error) {
        const bannerData = response.data.data || response.data;

        const newFormData = {
          banner_text: bannerData[0].banner_text || "",
          banner_image: null, // Keep as null for file input
          banner_status: Boolean(bannerData[0].banner_status),
          banner_order: bannerData[0].banner_order || "",
        };


        setFormData(newFormData);

        // Store existing image URL for display
        setExistingImageUrl(bannerData[0].banner_image || "");
      } else {
        SweetAlert.fire({
          title: "Error",
          text: "Failed to fetch banner data",
          icon: "error",
        });
      }
    } catch (error) {
      console.error(error);
      SweetAlert.fire({
        title: "Error",
        text: "An error occurred while fetching banner data",
        icon: "error",
      });
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      banner_image: file,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Prevent adding more than 4 banners
    if (!isEditMode && data.length >= 4) {
      SweetAlert.fire({
        title: "Limit Reached",
        text: "You can only add a maximum of 4 banners.",
        icon: "warning",
      });
      return;
    }

    if (!formData.banner_order.trim()) {
      SweetAlert.fire({
        title: "Validation Error",
        text: "Banner order is required",
        icon: "warning",
      });
      return;
    }

    // For add mode, image is required. For edit mode, image is optional
    if (!isEditMode && !formData.banner_image) {
      SweetAlert.fire({
        title: "Validation Error",
        text: "Banner image is required",
        icon: "warning",
      });
      return;
    }

    setIsLoading(true);

    const submitFormData = new FormData();
    submitFormData.append("banner_text", formData.banner_text);
    submitFormData.append("banner_status", formData.banner_status ? 1 : 0);
    submitFormData.append("banner_order", formData.banner_order);

    // For edit mode, always append the ID
    if (isEditMode) {
      submitFormData.append("id", id);
    }

    // Handle image data - SIMPLIFIED APPROACH
    if (formData.banner_image) {
      // New image selected - append the new image
      submitFormData.append("banner_image", formData.banner_image);
    } else if (isEditMode) {
      // Edit mode without new image - let backend know to keep existing
      // Option 1: Send the existing image URL
      if (existingImageUrl) {
        submitFormData.append("existing_image_url", existingImageUrl);
      }
      // Option 2: Send a flag (choose one approach based on your backend)
      submitFormData.append("keep_existing_image", "1");
    }

    for (let pair of submitFormData.entries()) {
      console.log(pair[0] + ": " + pair[1]);
    }

    const apiEndpoint = isEditMode ? "update-banner" : "save-banner";
    const successMessage = isEditMode
      ? "Banner updated successfully"
      : "Banner added successfully";

    axiosConfig
      .post(apiEndpoint, submitFormData, setHeader())
      .then((res) => {
        setIsLoading(false);

        if (!res.data.error) {
          SweetAlert.fire({
            title: "Success",
            text: res.data.messages || successMessage,
            icon: "success",
          });
          navigate('/pages/home-banners/banner-list')
          // Reset form after successful submission (only for add mode)
          if (!isEditMode) {
            //   handleReset();
          } else {
            // For edit mode, refresh the data to show updated info
            fetchBannerData();
          }
        } else {
          SweetAlert.fire({
            title: "Oops!",
            text:
              res.data.messages ||
              `Failed to ${isEditMode ? "update" : "add"} banner. Try again!`,
            icon: "error",
          });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Backend error:", error);
        SweetAlert.fire({
          title: "Error",
          text: `An error occurred while ${isEditMode ? "updating" : "adding"
            } the banner`,
          icon: "error",
        });
      });
  };



  if (isDataLoading || loading) {
    return (
      <div className="AddHomeBannerMainWrapper">
        <div className="banner-form-container">
          <div className="loading-state">Loading banner data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="AddHomeBannerMainWrapper">
      <div className="banner-form-container">
        <h2 className="form-title">
          {isEditMode ? "Edit Home Banner" : "Add Home Banner"}
        </h2>

        <form onSubmit={handleSubmit} className="banner-form">
          <div className="form-group">
            <label htmlFor="banner_text" className="form-label">
              Banner Text
            </label>
            <textarea
              id="banner_text"
              name="banner_text"
              value={formData.banner_text}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Enter banner text..."
              rows="3"
            // required
            />
          </div>
          <div className="form-group">
            <label htmlFor="banner_text" className="form-label">
              Banner Order
            </label>
            <input
              id="banner_order"
              name="banner_order"
              value={formData.banner_order}
              onChange={handleInputChange}
              className="input"
              placeholder="Enter banner order..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="banner_image" className="form-label">
              Banner Image <span>3300 x 1500</span> {isEditMode && "(Leave empty to keep current image)"}
            </label>

            {/* Show existing image in edit mode */}
            {isEditMode && existingImageUrl && (
              <div className="existing-image-preview">
                <img
                  src={existingImageUrl}
                  alt="Current banner"
                  style={{
                    maxWidth: "200px",
                    maxHeight: "100px",
                    objectFit: "cover",
                    marginBottom: "10px",
                  }}
                />
                <p className="existing-image-text">Current Image</p>
              </div>
            )}

            <input
              type="file"
              id="banner_image"
              name="banner_image"
              accept="image/*"
              required={!isEditMode}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  // Size check
                  if (file.size > 200 * 1024) {
                    SweetAlert.fire({
                      title: "Validation Error",
                      text: `File size must be 200KB or less.`,
                      icon: "warning",
                    });
                    e.target.value = null;
                    return;
                  }

                  // Width check
                  const img = new Image();
                  img.onload = () => {
                    if (img.height > 1500) {
                      SweetAlert.fire({
                        title: "Validation Error",
                        text: `Image height must be 1500px or less.`,
                        icon: "warning",
                      });
                      e.target.value = null;
                      return;
                    }
                    // Passed both checks
                    handleImageChange(e);
                  };
                  img.src = URL.createObjectURL(file);
                }
              }}
              className="form-file-input"
            />

            {/* Size requirement text */}
            <small style={{ fontSize: "12px", color: "#6c757d", display: "block", marginTop: "5px" }}>
              * Image size: 3300 x 1500 pixels | Max 200 KB
            </small>

            {/* Preview new selected image */}
            {formData.banner_image && (
              <div className="file-preview" style={{ marginTop: "8px" }}>
                New file selected: {formData.banner_image.name}
              </div>
            )}
          </div>


          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="banner_status"
                checked={formData.banner_status}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="checkbox-text">Active</span>
            </label>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading
                ? `${isEditMode ? "Updating" : "Submiting..."}`
                : `${isEditMode ? "Update" : "Submit"}`}
            </button>
            {/* <button
              type="button"
              onClick={handleReset}
              className="btn btn-secondary"
              disabled={isLoading}
            >
              Reset
            </button> */}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddHomeBanner;
